import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Loader2, Save } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

interface DayAvailability {
  day_of_week: number
  day_name: string
  is_available: boolean
  is_all_day: boolean
  start_time: string | null
  end_time: string | null
  slot_interval_minutes: number | null
  notes: string | null
}

interface AvailabilityData {
  days: DayAvailability[]
}

export default function AvailabilityEditor() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const [localDays, setLocalDays] = useState<DayAvailability[]>([])

  // Fetch availability
  const { data, isLoading } = useQuery<AvailabilityData>({
    queryKey: ['availability'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: AvailabilityData }>('/user/availability')
      return response.data.data
    },
  })

  useEffect(() => {
    if (data?.days) {
      setLocalDays(data.days)
    }
  }, [data])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (days: DayAvailability[]) => {
      await apiClient.put('/user/availability', { days })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability'] })
    },
  })

  const handleToggleDay = (dayOfWeek: number) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek
          ? {
              ...day,
              is_available: !day.is_available,
              // Reset times when becoming unavailable
              start_time: day.is_available ? null : '09:00',
              end_time: day.is_available ? null : '17:00',
            }
          : day
      )
    )
  }

  const handleToggleAllDay = (dayOfWeek: number) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek
          ? { ...day, is_all_day: !day.is_all_day }
          : day
      )
    )
  }

  const handleTimeChange = (
    dayOfWeek: number,
    field: 'start_time' | 'end_time',
    value: string
  ) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek ? { ...day, [field]: value } : day
      )
    )
  }

  const handleIntervalChange = (dayOfWeek: number, value: number | null) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek
          ? { ...day, slot_interval_minutes: value }
          : day
      )
    )
  }

  const handleNotesChange = (dayOfWeek: number, value: string) => {
    setLocalDays((prev) =>
      prev.map((day) =>
        day.day_of_week === dayOfWeek ? { ...day, notes: value || null } : day
      )
    )
  }

  const handleSave = () => {
    updateMutation.mutate(localDays)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const hasChanges = JSON.stringify(data?.days) !== JSON.stringify(localDays)

  return (
    <div className="p-6 lg:p-8">
      <div className={`flex items-center justify-between ${embedded ? 'mb-6' : 'mb-8'}`}>
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('availability.title')}</h1>
            <p className="text-gray-500">{t('availability.subtitle')}</p>
          </div>
        )}
        {embedded && <div />}
        <Button onClick={handleSave} disabled={!hasChanges || updateMutation.isPending}>
          {updateMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('availability.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('availability.saveChanges')}
            </>
          )}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('availability.schedule.title')}</CardTitle>
          <CardDescription>
            {t('availability.schedule.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {localDays.map((day) => (
              <div
                key={day.day_of_week}
                className={`p-4 rounded-lg border ${
                  day.is_available ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={day.is_available}
                      onChange={() => handleToggleDay(day.day_of_week)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="font-semibold text-gray-900">{day.day_name}</span>
                  </div>

                  {day.is_available && (
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={day.is_all_day}
                          onChange={() => handleToggleAllDay(day.day_of_week)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        {t('availability.schedule.allDay')}
                      </label>
                    </div>
                  )}
                </div>

                {day.is_available && !day.is_all_day && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ml-8">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {t('availability.schedule.open')}
                      </label>
                      <input
                        type="time"
                        value={day.start_time || ''}
                        onChange={(e) =>
                          handleTimeChange(day.day_of_week, 'start_time', e.target.value)
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {t('availability.schedule.close')}
                      </label>
                      <input
                        type="time"
                        value={day.end_time || ''}
                        onChange={(e) =>
                          handleTimeChange(day.day_of_week, 'end_time', e.target.value)
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {t('availability.schedule.slotInterval')}
                      </label>
                      <select
                        value={day.slot_interval_minutes || ''}
                        onChange={(e) =>
                          handleIntervalChange(
                            day.day_of_week,
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="">{t('availability.schedule.noSlots')}</option>
                        <option value="15">{t('availability.schedule.minutes15')}</option>
                        <option value="30">{t('availability.schedule.minutes30')}</option>
                        <option value="60">{t('availability.schedule.hour1')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        {t('availability.schedule.notes')}
                      </label>
                      <input
                        type="text"
                        value={day.notes || ''}
                        onChange={(e) => handleNotesChange(day.day_of_week, e.target.value)}
                        placeholder={t('availability.schedule.notesPlaceholder')}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                  </div>
                )}

                {day.is_available && day.is_all_day && (
                  <div className="ml-8 text-sm text-gray-500">
                    {t('availability.schedule.open24hours')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t('availability.preview.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600">
            {localDays.filter((d) => d.is_available).length === 0 ? (
              <p>{t('availability.preview.noAvailability')}</p>
            ) : (
              <p>
                {localDays
                  .filter((d) => d.is_available)
                  .map((d) =>
                    d.is_all_day
                      ? t('availability.preview.formatAllDay', { day: d.day_name })
                      : d.start_time && d.end_time
                      ? t('availability.preview.formatHours', { day: d.day_name, start: d.start_time, end: d.end_time })
                      : t('availability.preview.formatOpen', { day: d.day_name })
                  )
                  .join(' | ')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
